import { create } from 'zustand';
import api from '../services/api';
import ws from '../services/wsService';

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'operator' | 'admin' | 'superadmin';
  isActive: boolean;
  isOnline: boolean;
  avatar?: string;
  department?: string;
  supportLine?: number; // Линия поддержки (1 или 2)
  canHandleEscalations?: boolean; // Может принимать заявки 2-й линии
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // role intentionally absent — pinned to 'operator' server-side. Admin/superadmin
  // accounts are created only via POST /api/users/ (superadmin-gated).
  department?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<any>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  getProfile: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  login: async (email: string, password: string) => {
    // Проверяем, не выполняется ли уже вход
    if (get().isLoading) {
      throw new Error('Вход уже выполняется');
    }

    try {
      set({ isLoading: true, error: null });
      
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });

      const payload = response.data?.data || response.data || {};
      const token = payload.token;
      const refresh = payload.refresh;
      const user = payload.user;
      
      if (!token || !user) {
        throw new Error('Неверный формат ответа сервера');
      }
      
      localStorage.setItem('token', token);
      if (refresh) localStorage.setItem('refreshToken', refresh);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Connect WebSocket with the new token
      ws.connect(token);
      ws.joinDashboard();

      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Ошибка входа';
      set({
        error: message,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
      });
      localStorage.removeItem('token');
      throw error;
    }
  },

  logout: () => {
    // Отзываем refresh-токен этого устройства на сервере (best-effort, не ждём).
    try {
      const refresh = localStorage.getItem('refreshToken');
      if (refresh) api.post('/api/auth/logout', { refresh }).catch(() => {});
    } catch { /* ignore */ }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete api.defaults.headers.common['Authorization'];
    ws.disconnect();
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });
  },

  register: async (data: RegisterData) => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.post('/api/auth/register', data);

      if (response.data.success) {
        set({ isLoading: false, error: null });
        // Return the payload so the page can branch into the Telegram
        // verification screen (data.requiresTelegramVerification) instead
        // of redirecting straight to login.
        return response.data.data || {};
      } else {
        throw new Error(response.data.message || 'Ошибка регистрации');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Ошибка регистрации';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateProfile: async (data: Partial<User>) => {
    try {
      set({ isLoading: true, error: null });
      
      const response = await api.put('/api/auth/profile', data);
      const updatedUser = response.data.data.user;
      
      set({
        user: updatedUser,
        isLoading: false,
      });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Ошибка обновления профиля';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    try {
      set({ isLoading: true, error: null });
      
      await api.put('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      
      set({ isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Ошибка изменения пароля';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const response = await api.post('/api/auth/refresh', { refresh: refreshToken });
      const { token, user } = response.data.data || response.data;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Обновляем токен в WS-сервисе, чтобы будущие переподключения шли с валидным токеном
      ws.connect(token);

      set({
        user,
        token,
        isAuthenticated: true,
      });
    } catch (error) {
      get().logout();
      throw error;
    }
  },

  getProfile: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get('/api/auth/me');
      const user = response.data.data.user;
      set({ user, isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Ошибка загрузки профиля';
      set({ error: message, isLoading: false });
      throw error;
    }
  },
}));

// Инициализация токена при загрузке приложения
const token = localStorage.getItem('token');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  // Подключаем WebSocket для уже залогиненной сессии (после перезагрузки login() не вызывается,
  // поэтому без этого live-обновления не приходили до ручного refresh).
  ws.connect(token);
  // Загружаем профиль пользователя
  const store = useAuthStore.getState();
  store.getProfile().catch((error: any) => {
    const status = error?.response?.status;
    // Сбрасываем токен только если он реально невалидный/просроченный.
    if (status === 401 || status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      delete api.defaults.headers.common['Authorization'];
      useAuthStore.setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      return;
    }

    // На сетевых/таймаут ошибках не разлогиниваем — пусть пользователь сможет повторить.
    useAuthStore.setState({
      user: null,
      token,
      isAuthenticated: true,
      isLoading: false,
      error: 'Не удалось загрузить профиль. Проверьте соединение и попробуйте ещё раз.',
    });
  });
}